import { Banner } from 'component/banner';
import { Pad, Separator } from 'component/basics';
import { ConversationScreen } from 'component/screen';
import { Heading, UtilityText } from 'component/text';
import { useVideoVotingDisabled } from 'data/video-votings';
import { OverlayContainerContextProvider } from 'open-modules/zdf/component/overlay';
import { DemoVideoPlayerContainer } from 'open-modules/zdf/component/videoplayer/videoplayer-demo';
import { useVideo, VideoPlayerStateContextProvider } from 'open-modules/zdf/component/videoplayer/videoplayer-state';
import type { VideoPlayerState } from 'open-modules/zdf/component/videoplayer/videoplayer-types';
import { VideoVotingEditor } from 'open-modules/zdf/component/videovoting/videovoting-editor';
import { VideoVotingEndScreen } from 'open-modules/zdf/component/videovoting/videovoting-end-screen';
import { VideoVotingIntroScreen } from 'open-modules/zdf/component/videovoting/videovoting-intro-screen';
import { VideoVotingOverlay } from 'open-modules/zdf/component/videovoting/videovoting-overlay';
import { VideoVotingQuestionDialog } from 'open-modules/zdf/component/videovoting/videovoting-question-dialog';
import type { VideoVoting, VideoVotingQuestion } from 'open-modules/zdf/component/videovoting/videovoting-types';
import {
    useCurrentQuestion,
    useVideoVoting,
    VideoVotingSessionInformationBox,
} from 'open-modules/zdf/component/videovoting/videovoting-util';
import type { VideoVotingSessionData } from 'open-modules/zdf/videovoting/videoVotingSession';
import {
    setVideoVotingSessionData,
    updateVideoVotingSessionData,
    useVideoVotingSessionData,
    VIDEO_VOTING_SESSION_STATES,
} from 'open-modules/zdf/videovoting/videoVotingSession';
import { useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { useIsAdmin } from 'util/admin';
import { useDatastore, useInstanceKey, usePersonaKey } from 'util/datastore';
import { t } from 'util/stdtypes';
import { generateRandomKey } from 'util/util';

/*
Figma:
https://www.figma.com/design/2LvWUQydUpat6ohKwYM12u/Video-Voting-Tool---Testing?node-id=20-5&t=5DeU2AZDvMXzjf7G-0
*/

/**
 * @expected-unused Loaded via a dynamic import in
 * client/app/[siloKey]/(app)/videovoting/[instanceKey]/_layout.tsx; fallow
 * can't trace the `.then((m) => m.X)` binding.
 */
export const VideoVotingStructure = {
    key: 'videovoting',
    name: 'Video Voting',
    screen: VideoVotingSidebarScreen,
    overlayScreen: VideoVotingTeaser,
    subscreens: {
        edit: EditVideoVotingScreen,
        debug: VideoVotingDemoScreen,
    },
    receivesConfigDefaults: {},
};

function VideoVotingSidebarScreen() {
    return <UtilityText label={t('video_voting.no_main_screen', 'No video voting main screen defined.')} />;
}

/** @public */
export function VideoVotingTeaser() {
    return (
        <OverlayContainerContextProvider reflectScreenPropertiesOntoIframe repositionAndResizeIframe>
            <VideoPlayerStateContextProvider useVideoFromArticle={true}>
                <VideoVoting />
            </VideoPlayerStateContextProvider>
        </OverlayContainerContextProvider>
    );
}

// Helper component to make video voting demoable It contains a fake video UI
// und prints the video voting session state
type DemoVideoVotingProps = {
    showStats?: boolean;
    videoState?: VideoPlayerState;
};
export function DemoVideoVoting({ showStats = true, videoState }: DemoVideoVotingProps) {
    return (
        <View>
            <DemoVideoPlayerContainer videoState={videoState}>
                <VideoVoting />
            </DemoVideoPlayerContainer>
            {showStats && (
                <View>
                    <Separator />
                    <Heading label={t('video_voting.current_session_state', 'Current Session State Object')} />
                    <VideoVotingSessionInformationBox />
                </View>
            )}
        </View>
    );
}

/** @public */
export function VideoVotingDemoScreen() {
    const isAdmin = useIsAdmin();
    if (!isAdmin) return null;
    return (
        <View>
            <DemoVideoVoting showStats={true} />
        </View>
    );
}

export type VideoVotingProps = {
    votingKey?: string;
};
export function VideoVoting({ votingKey }: VideoVotingProps) {
    const instanceKey = useInstanceKey();
    const currentSession = useVideoVotingSessionData();

    return (
        <>
            <VideoVotingInitializer votingKey={votingKey ?? instanceKey} />
            {currentSession && (
                <>
                    <VideoVotingSessionController />
                    <VideoVotingScreen />
                </>
            )}
        </>
    );
}

function VideoVotingScreen() {
    const currentSession = useVideoVotingSessionData();
    const videoVoting = useVideoVoting(currentSession.votingKey);
    const currentQuestion = useCurrentQuestion();
    const question = getQuestionFromVoting(videoVoting, currentQuestion?.key);

    if (!videoVoting || !currentSession) {
        return (
            <Banner>
                <Heading label={t('video_voting.template_missing', 'The video voting template is missing')}></Heading>
            </Banner>
        );
    }

    if (currentSession.state === VIDEO_VOTING_SESSION_STATES.initVideo) {
        return (
            <VideoVotingOverlay pointerEvents={false} visible={false}>
                <UtilityText label={t('video_voting.waiting_for_video', 'Waiting for video to load!')}></UtilityText>
            </VideoVotingOverlay>
        );
    }

    if (currentSession.state === VIDEO_VOTING_SESSION_STATES.initSession) {
        return <VideoVotingIntroScreen></VideoVotingIntroScreen>;
    }

    if (
        currentSession.state === VIDEO_VOTING_SESSION_STATES.waitForQuestion &&
        question &&
        !currentSession?.questions?.[question?.key]?.answered
    ) {
        return <VideoVotingQuestionDialog key={question.key} question={question} />;
    }

    if (
        (currentSession.state === VIDEO_VOTING_SESSION_STATES.waitForQuestion ||
            currentSession.state === VIDEO_VOTING_SESSION_STATES.endScreen) &&
        !currentSession.teaserDismissed
    ) {
        return <VideoVotingEndScreen></VideoVotingEndScreen>;
    }
    return (
        <VideoVotingOverlay pointerEvents={false} visible={false}>
            <UtilityText label={t('video_voting.no_question_overlay', 'No question overlay shown')}></UtilityText>
        </VideoVotingOverlay>
    );
}
function getQuestionFromVoting(voting?: VideoVoting, questionKey?: string): VideoVotingQuestion | undefined {
    if (!voting || !questionKey || !voting?.questions) return;
    for (const question of voting.questions) {
        if (question.key === questionKey) {
            return question;
        }
    }
}

// This component sets up the video voting session data It handles cleanups and
// resets in case the user changes, etc
type VideoVotingInitializerProps = {
    votingKey: string;
};

function VideoVotingInitializer({ votingKey }: VideoVotingInitializerProps) {
    const datastore = useDatastore();
    const videoVotingSession = useVideoVotingSessionData();
    const videoVoting = useVideoVoting(votingKey);
    const personaKey = usePersonaKey();

    const votingDisabledGlobally = useVideoVotingDisabled();

    const initializeSession = useCallback(() => {
        const initialSessionData: VideoVotingSessionData = {
            state: votingDisabledGlobally ? VIDEO_VOTING_SESSION_STATES.disabled : VIDEO_VOTING_SESSION_STATES.initVideo,
            anonymous: false,
            questions: {},
            personaKey: personaKey,
            votingKey,
            voting: videoVoting,
            id: generateRandomKey(10),
        };
        setVideoVotingSessionData(datastore, initialSessionData);
    }, [datastore, personaKey, videoVoting, votingKey, votingDisabledGlobally]);

    useEffect(() => {
        // Initialize with an empty session When the user changes, we have to
        // reset the session
        if (videoVoting && (!videoVotingSession || (videoVotingSession.personaKey && videoVotingSession.personaKey !== personaKey))) {
            initializeSession();
            return;
        }

        // Case: user changes or the global setting changes -> Reset session
        if (
            videoVotingSession &&
            (videoVotingSession.personaKey !== personaKey ||
                (votingDisabledGlobally && videoVotingSession.state !== VIDEO_VOTING_SESSION_STATES.disabled) ||
                (!votingDisabledGlobally && videoVotingSession.state === VIDEO_VOTING_SESSION_STATES.disabled))
        ) {
            initializeSession();
        }
    }, [videoVotingSession, personaKey, videoVoting, votingDisabledGlobally, initializeSession]);

    return null;
}

// This component handles the control flow of the video voting It reacts to
// session changes
function VideoVotingSessionController() {
    const datastore = useDatastore();
    const videoVotingSession = useVideoVotingSessionData();
    const video = useVideo();

    // State machine: React to changing states
    useEffect(() => {
        if (videoVotingSession.state === VIDEO_VOTING_SESSION_STATES.initVideo && video.ready) {
            updateVideoVotingSessionData(datastore, { state: VIDEO_VOTING_SESSION_STATES.initSession });
        }
    }, [videoVotingSession.state, video.ready, datastore]);

    return null;
}

function EditVideoVotingScreen() {
    const instanceKey = useInstanceKey();
    const myVideoVoting = useVideoVoting(instanceKey);

    return (
        <>
            {myVideoVoting && (
                <ConversationScreen pad>
                    <Heading label={t('video_voting.edit_title', 'Edit Video voting')} type='large' />
                    <Pad />
                    {myVideoVoting && <VideoVotingEditor voting={myVideoVoting} />}
                </ConversationScreen>
            )}
        </>
    );
}
